'use client'

import type { Token } from '@polaris/core/models'
import { cva, type VariantProps } from 'class-variance-authority'
import { type ComponentProps, type ReactNode, useMemo } from 'react'
import type { useTokenInfoAssetData } from '@/src/queries/hooks/token-info/asset-data'
import { cn } from '@/src/utils/styles'
import { PriceDisplay } from '../../price-display'
import { TruncatedText } from '../../truncated-text'
import { PriceChange as AppPriceChange } from '../../ui/price-change'
import { Skeleton } from '../../ui/skeleton'
import TokenImg from '../../ui/token-img'
import type { ChartHoverData } from '.'

const headerTokenImgVariants = cva('size-6', {
  variants: {
    variant: {
      default: 'mt-1',
      'compact-on-mobile': '@lg:mt-1',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const headerHeaderVariants = cva('flex gap-3 min-h-6', {
  variants: {
    variant: {
      default: 'items-start',
      'compact-on-mobile': 'items-center @lg:items-start',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const headerDisplayNameVariants = cva(
  'min-w-0 max-h-full whitespace-nowrap text-primary overflow-hidden font-semibold leading-8 text-ellipsis @lg:text-xl',
  {
    variants: {
      variant: {
        default: '',
        'compact-on-mobile': 'hidden @lg:block',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

const headerFooterVariants = cva('min-h-[5.5rem]', {
  variants: {
    variant: {
      default: '',
      'compact-on-mobile': 'hidden @lg:block',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

interface ChartHeaderProps extends ComponentProps<'div'> {
  chartHoverData?: ChartHoverData
  tokenData?: Token | undefined
  isLoading?: boolean
  tokenInfoAssetData: ReturnType<typeof useTokenInfoAssetData>['data']
  isAssetDataLoading?: boolean
  isChartDataLoading?: boolean
  priceChange: number
  ticker: string
  cta?: ReactNode
  variant?: VariantProps<typeof headerDisplayNameVariants>['variant']
}

export default function ChartHeader({
  chartHoverData,
  isLoading,
  tokenData,
  tokenInfoAssetData,
  isAssetDataLoading,
  isChartDataLoading,
  priceChange,
  ticker,
  cta,
  className,
  variant = 'default',
}: ChartHeaderProps) {
  // isLoading = true
  return (
    <div className={cn('flex flex-col gap-1 min-w-0', className)}>
      <div className={cn(headerHeaderVariants({ variant }))}>
        {!isLoading ? (
          <TokenImg
            src={tokenData?.logo_img_url}
            ticker={ticker}
            width={24}
            height={24}
            className={cn(headerTokenImgVariants({ variant }))}
            fallbackContainerClassName='size-6'
            charLimit={1}
            unoptimized
          />
        ) : (
          <Skeleton className='size-6 rounded-full' />
        )}
        <div className='flex items-baseline gap-x-2 flex-wrap overflow-hidden'>
          <TruncatedText
            as='h6'
            className={cn('min-w-0', headerDisplayNameVariants({ variant }))}
          >
            {tokenData?.display_name}
          </TruncatedText>
          <TruncatedText
            as='span'
            className={cn('text-tertiary min-w-0', {
              '@lg:block hidden': variant === 'compact-on-mobile',
            })}
          >
            {tokenData?.ticker}
          </TruncatedText>
          <TruncatedText
            as='span'
            className={cn('font-semibold text-primary max-w-full hidden', {
              'block @lg:hidden': variant === 'compact-on-mobile',
            })}
            forceTooltip
            stringLimit={4}
          >
            {tokenData?.ticker}
          </TruncatedText>
        </div>
      </div>
      <div className={cn(headerFooterVariants({ variant }))}>
        {!isLoading && !isAssetDataLoading ? (
          <Price
            hoverPrice={chartHoverData}
            marketPrice={tokenInfoAssetData?.market_data.current_price.usd ?? 0}
          />
        ) : (
          <div className='mt-3 pb-3'>
            <Skeleton className='w-40 h-10' />
          </div>
        )}
        {!isAssetDataLoading && !isChartDataLoading ? (
          <div className='relative'>
            <div
              className={cn('flex items-center gap-2', {
                'opacity-100 group-hover:opacity-0 transition-opacity duration-500':
                  cta !== undefined,
              })}
            >
              <PriceChange
                chartHover={chartHoverData}
                selectedTimeFramePriceChange={priceChange}
              />
              <HoverTimestamp chartHover={chartHoverData} />
            </div>
            {cta !== undefined && (
              <div className='absolute top-0 left-0 bottom-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500'>
                {cta}
              </div>
            )}
          </div>
        ) : (
          <div className='pt-2'>
            <Skeleton className='w-24 h-4 rounded-[8px]' />
          </div>
        )}
      </div>
    </div>
  )
}

function Price({
  hoverPrice,
  marketPrice,
}: {
  hoverPrice?: ChartHoverData
  marketPrice: number
}) {
  const price = hoverPrice?.price || marketPrice || 0

  return (
    <h3
      className={cn(
        'text-4xl md:text-5xl font-serif font-extralight text-primary md:mt-1',
        {
          'text-tertiary': !price,
        },
      )}
    >
      <PriceDisplay price={price} />
    </h3>
  )
}

function PriceChange({
  chartHover,
  selectedTimeFramePriceChange,
}: {
  chartHover?: ChartHoverData
  selectedTimeFramePriceChange: number
}) {
  const percentChange = useMemo(() => {
    if (!chartHover) return selectedTimeFramePriceChange ?? 0

    const hoveredPrice = chartHover.price
    const firstPriceInSequence = chartHover.firstPriceInSequence

    return ((hoveredPrice - firstPriceInSequence) / firstPriceInSequence) * 100
  }, [chartHover, selectedTimeFramePriceChange])

  return <AppPriceChange hideIfZero={false} change={percentChange} size='md' />
}

function HoverTimestamp({ chartHover }: { chartHover?: ChartHoverData }) {
  return (
    <span
      className={cn('text-tertiary opacity-0 transition-opacity', {
        'opacity-100': !!chartHover,
      })}
    >
      {chartHover?.time}
    </span>
  )
}
